import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageHeader from "../../../../app/layout/PageHeader";
import { api, ApiError } from "../../../../api/apiClient";
import "../../../../css/Calendar.css";
import CalendarListView from "./CalendarListView";
import CalendarMonthView from "./CalendarMonthView";
import CalendarServiceFilter from "./CalendarServiceFilter";
import CalendarToolbar from "./CalendarToolbar";
import CalendarWeekView from "./CalendarWeekView";
import MobileCalendar from "./MobileCalendar";
import SelectedDayPanel from "./SelectedDayPanel";
import { CalendarService, CalendarServiceInstance, startOfMonthGrid, startOfWeek } from "./calendarUtils";
import { useServiceCalendar } from "./useServiceCalendar";

interface Props {
  tenantId: number;
  serviceId?: number | null;
  onBack?: () => void;
  onOpenInstance?: (serviceId: number, serviceInstanceId: number) => void;
}

function useMobileCalendarLayout() {
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setMobile(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return mobile;
}

export default function ServiceCalendarTab({ tenantId, serviceId, onBack, onOpenInstance }: Props) {
  const isMobile = useMobileCalendarLayout();
  const [services, setServices] = useState<CalendarService[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>(serviceId ? [serviceId] : []);
  const [instances, setInstances] = useState<CalendarServiceInstance[]>([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const calendar = useServiceCalendar(isMobile);

  useEffect(() => {
    let active = true;
    setServicesLoading(true);
    setError(null);
    api<CalendarService[]>("/api/ServicesCalendar/available-services", { suppressLoading: true })
      .then((result) => {
        if (!active) return;
        setServices(result);
        setSelectedServiceIds(serviceId ? [serviceId] : result.map((service) => service.serviceId));
      })
      .catch((reason) => active && setError(reason as ApiError))
      .finally(() => active && setServicesLoading(false));
    return () => { active = false; };
  }, [tenantId, serviceId]);

  const loadCalendar = useCallback(() => {
    if (servicesLoading || selectedServiceIds.length === 0) {
      setInstances([]);
      return () => undefined;
    }
    let active = true;
    setCalendarLoading(true);
    setError(null);
    api<CalendarServiceInstance[]>("/api/ServicesCalendar/search", {
      method: "POST",
      body: { serviceIds: selectedServiceIds, fromDate: calendar.fromDate, toDate: calendar.toDate },
      suppressLoading: true,
    })
      .then((result) => active && setInstances(result))
      .catch((reason) => active && setError(reason as ApiError))
      .finally(() => active && setCalendarLoading(false));
    return () => { active = false; };
  }, [calendar.fromDate, calendar.toDate, selectedServiceIds, servicesLoading]);

  useEffect(() => loadCalendar(), [loadCalendar, reloadKey]);

  const filteredInstances = useMemo(
    () => instances.filter((instance) => selectedServiceIds.includes(instance.serviceId)),
    [instances, selectedServiceIds],
  );
  const selectedService = services.find((service) => service.serviceId === serviceId);
  const openInstance = (instance: CalendarServiceInstance) => onOpenInstance?.(instance.serviceId, instance.serviceInstanceId);

  return (
    <div className="service-calendar-page">
      <PageHeader
        title="Service Calendar"
        subtitle={serviceId ? selectedService?.name || "Service schedule" : "Review scheduled services across your organisation."}
        actions={onBack ? <button type="button" className="icon-button-muted-solid" onClick={onBack} title="Back to service" aria-label="Back to service"><ArrowBackIcon fontSize="small" /></button> : undefined}
      />

      {servicesLoading ? <div className="calendar-state">Loading calendar…</div> : services.length === 0 ? <div className="calendar-empty"><strong>No services available</strong><span>There are currently no services available for this calendar.</span></div> : <>
        <CalendarServiceFilter services={services} selectedIds={selectedServiceIds} onChange={setSelectedServiceIds} singleServiceMode={Boolean(serviceId)} />

        {error && <div className="calendar-error" role="alert"><span>We couldn't load the calendar. {error.message}</span><button type="button" onClick={() => setReloadKey((value) => value + 1)}>Retry</button></div>}

        {selectedServiceIds.length === 0 ? <div className="calendar-empty"><strong>No services selected</strong><span>Choose one or more services to display their scheduled activities.</span></div> : <>
          {calendarLoading && <div className="calendar-loading" role="status">Loading calendar…</div>}
          <div className="calendar-desktop">
            <div className="calendar-desktop-main">
              <CalendarToolbar view={calendar.desktopView} selectedDate={calendar.selectedDate} onViewChange={calendar.setDesktopView} onNavigate={calendar.navigate} onToday={calendar.goToToday} />
              {calendar.desktopView === "week" && <CalendarWeekView weekStart={startOfWeek(calendar.selectedDate)} selectedDate={calendar.selectedDate} instances={filteredInstances} onSelectDate={calendar.setSelectedDate} onOpen={openInstance} />}
              {calendar.desktopView === "month" && <CalendarMonthView gridStart={startOfMonthGrid(calendar.selectedDate)} selectedDate={calendar.selectedDate} instances={filteredInstances} onSelectDate={calendar.setSelectedDate} onOpen={openInstance} />}
              {calendar.desktopView === "list" && <CalendarListView weekStart={startOfWeek(calendar.selectedDate)} instances={filteredInstances} onSelectDate={calendar.setSelectedDate} onOpen={openInstance} />}
            </div>
            <SelectedDayPanel selectedDate={calendar.selectedDate} instances={filteredInstances} onOpen={openInstance} />
          </div>
          <MobileCalendar view={calendar.mobileView} selectedDate={calendar.selectedDate} instances={filteredInstances} onViewChange={calendar.setMobileView} onSelectDate={calendar.setSelectedDate} onNavigate={calendar.navigate} onToday={calendar.goToToday} onOpen={openInstance} />
        </>}
      </>}
    </div>
  );
}
